/**
 * Utility functions to manage recently viewed products in localStorage
 */

const STORAGE_KEY = 'recently_viewed_products';
const MAX_ITEMS = 20; // Maximum number of products to keep in history

export interface RecentlyViewedProduct {
  slug: string;
  name: string;
  main_image_url?: string;
  price?: number;
  final_price?: number;
  viewedAt: number; // timestamp
  min_order_qty?: number;
  min_logo_qty?: number;
  delivery_date?: number;
}

/**
 * Get all recently viewed products
 */
export const getRecentlyViewedProducts = (): RecentlyViewedProduct[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const products: RecentlyViewedProduct[] = JSON.parse(stored);
    // Sort by viewedAt descending (most recent first)
    return products.sort((a, b) => b.viewedAt - a.viewedAt);
  } catch (error) {
    console.error('Error reading recently viewed products:', error);
    return [];
  }
};

/**
 * Add a product to recently viewed list
 */
export const addRecentlyViewedProduct = (product: {
  slug: string;
  name: string;
  main_image_url?: string;
  price?: number;
  final_price?: number;
}): void => {
  try {
    const products = getRecentlyViewedProducts();
    
    // Remove if already exists (to update timestamp)
    const filtered = products.filter(p => p.slug !== product.slug);
    
    // Add new product at the beginning
    const newProduct: RecentlyViewedProduct = {
      ...product,
      viewedAt: Date.now()
    };
    
    const updated = [newProduct, ...filtered];
    
    // Keep only the most recent MAX_ITEMS
    const limited = updated.slice(0, MAX_ITEMS);
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(limited));
  } catch (error) {
    console.error('Error adding recently viewed product:', error);
  }
};

/**
 * Remove a product from recently viewed list
 */
export const removeRecentlyViewedProduct = (slug: string): void => {
  try {
    const products = getRecentlyViewedProducts();
    const filtered = products.filter(p => p.slug !== slug);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Error removing recently viewed product:', error);
  }
};

/**
 * Clear all recently viewed products
 */
export const clearRecentlyViewedProducts = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing recently viewed products:', error);
  }
};

/**
 * Get recently viewed products excluding current product
 */
export const getRecentlyViewedProductsExcluding = (currentSlug: string): RecentlyViewedProduct[] => {
  const products = getRecentlyViewedProducts();
  return products.filter(p => p.slug !== currentSlug);
};

